import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
    CUSTOM_ELEMENTS_SCHEMA,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Signal,
    ViewChild,
    ViewEncapsulation,
    computed,
    effect,
    input,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTreeModule } from '@angular/material/tree';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { ContextMenuItemClickData } from '@em4cloud/my-cdk';
import {
    FolderType,
    ProcessFlatTreeNode,
    ProcessTreeComponent,
    ProcessTreeNode,
    TypeOfProcess,
    TypeOfProcessTreeNode,
} from '@em4cloud/process-tree';
import { Translation, TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Area, BpmUniversalDataObject, Process, TestScenario } from 'app/api';
import { AlertService } from 'app/core/alert/alert.service';
import { RoleService } from 'app/core/role-management/role-management.service';
import { SaveButtonService } from 'app/core/save-button/save-button.service';
import { BpReportService } from 'app/modules/bp-report/bp-report.service';
import { AreaService } from 'app/modules/org-sys-area/services/area.service';
import { TaskProcessService } from 'app/modules/task/task-process.service';
import { TestScenariosService } from 'app/modules/test/manage-scenarios/test-scenarios/test-scenarios.service';
import { TestService } from 'app/modules/test/test.service';
import { VerifyService } from 'app/modules/verify/verify.service';
import { ViewService } from 'app/modules/view/view.service';
import { BusinessProcessTaskService } from 'app/services/bp/business-process.service';
import { OrgNavigationService } from 'app/services/org-nav.service';
import { LanguageChangerService } from 'app/services/translate_api/languageChanger.service';
import { Observable, Subject, map } from 'rxjs';
import { ExtendedArea } from '../../../../../../projects/flowchart/src/lib/svgjs/svg-area/svg-area.model';
import { ProcessMenuService } from '../../process-menu.service';
import { ProcessService } from '../../process.service';
import { ProcessesFilterComponent } from '../processes-filter/processes-filter.component';
import { ProcessTreeService } from './processes-tree.service';

@Component({
    selector: 'processes-tree',
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        MatButtonModule,
        MatMenuModule,
        MatTreeModule,
        MatIconModule,
        DragDropModule,
        MatCheckboxModule,
        TranslocoModule,
        ProcessesFilterComponent,
        ProcessTreeComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    exportAs: 'processes-tree',
    templateUrl: './processes-tree.component.html',
    styleUrls: ['./processes-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessesTreeComponent implements OnInit, OnDestroy {
    @Input() type: TypeOfProcess;
    @Input() bpServiceType: 'task' | 'view' | 'verify' | 'test' | 'bpReport';
    @Input() baseUrl: string[];

    editMode = input(false);

    @ViewChild('processTree') processTree: ProcessTreeComponent<
        Process,
        BpmUniversalDataObject,
        Area
    >;

    processData = computed<Process[]>(() => this._processService.filteredState);
    testScenarioData = computed<BpmUniversalDataObject[]>(() => this._testService.testScenarios);
    mdoAreas = computed<Area[]>(() => {
        const areas = this._areaService.state()?.filter(area => area.status === 'ACTIVE');
        return areas?.length ? [...areas] : [];
    });

    selectedProcess = computed(this._processService.selectedProcess);

    translocoButtonContent: Signal<Translation>;
    componentName = 'ProcessesTree';

    protected bpService: BusinessProcessTaskService;

    previousBpId: string | null = null;

    transformFunction = signal((node: ProcessTreeNode, level: number): ProcessFlatTreeNode => {
        let flatNode: ProcessFlatTreeNode = {
            id: node.id,
            type: node.type,
            folderType: node.folderType,
            expandable: !!node.children,
            level: level,
            title: node.title,
            status: node.meta?.status,
            area: node.meta?.area,
            childrenCount: node.meta?.childrenCount,
            versionCount: node.meta?.versionCount,
        };
        if (node.type === TypeOfProcessTreeNode.Scenario) {
            flatNode.title = `${node.title} Scenario`;
            flatNode.checkbox = true;
            flatNode.icon = 'assignment';
        } else if (node.type === TypeOfProcessTreeNode.ConfigurationProcess) {
            flatNode.title = node.title;
            flatNode.icon = 'feather:settings';
            flatNode.expandable = node.children?.length > 0;
            flatNode.checkbox = true;
        } else if (node.type === TypeOfProcessTreeNode.Folder) {
            flatNode.title = node.title;
            flatNode.icon = node.folderType === FolderType.Area ? 'tree:area' : 'tree:process';
        } else if (node.type === TypeOfProcessTreeNode.Process) {
            flatNode.title = node.title;
            flatNode.expandable = node.children?.length > 0;
            flatNode.checkbox = true;
        } else if (node.type === TypeOfProcessTreeNode.SubProcess) {
            flatNode.title = node.title;
            flatNode.icon = 'tree:process';
            flatNode.expandable = node.children?.length > 0;
            flatNode.checkbox = true;
        }

        return flatNode;
    });

    constructor(
        protected _processService: ProcessService,
        protected _processTreeService: ProcessTreeService,
        protected inject: Injector,
        protected _translocoService: TranslocoService,
        protected _processMenuService: ProcessMenuService,
        protected _testScenarioService: TestScenariosService,
        protected _testService: TestService,
        protected route: ActivatedRoute,
        protected orgNav: OrgNavigationService,
        protected _destroyRef: DestroyRef,
        protected languageChangerService: LanguageChangerService,
        protected _areaService: AreaService,
        protected roleService: RoleService,
        protected alertService: AlertService,
        protected changeDetector: ChangeDetectorRef,
        protected _saveButtonService?: SaveButtonService
    ) {
        this.languageChangerService.register(this.componentName, this.ngOnInit.bind(this));

        effect(() => {
            this.roleService.userRoles();
            this.ngOnInit();
        });

        // setting transloco content for buttons
        this.translocoButtonContent = toSignal(
            this._translocoService.selectTranslateObject('buttons')
        );

        effect(() => {
            if (
                !this._areaService.isSelectionDialogOpened() &&
                this.selectedProcess() &&
                this.selectedProcess().area &&
                (this.orgNav.url.includes('processes') || this.orgNav.url.includes('report'))
            ) {
                const root = this._areaService.findRootParentNode(this.selectedProcess().area);

                let modifiedRoot = { ...root };

                modifiedRoot = this.removeProcessesAndPlaceholders([modifiedRoot])[0];

                if (modifiedRoot.id !== this.previousId) {
                    this.maxDepth = undefined;
                }

                modifiedRoot = this.setProcessesForArea(modifiedRoot);
                this.previousId = modifiedRoot.id;

                this._areaService.selectedItem = modifiedRoot;
            }
        });

        let isSubscribedToParamChanges = false;
        effect(() => {
            if (this.processData().length && !isSubscribedToParamChanges) {
                this.subscribeToParamChange();
            }
        });
    }

    protected removeProcessesAndPlaceholders(areas: ExtendedArea[]): ExtendedArea[] {
        areas.forEach(area => {
            area.processes = [];
            area.children = area.children?.filter(child => child.id !== 'placeholder');

            if (area.children?.length) {
                area.children = this.removeProcessesAndPlaceholders(area.children);
            }
        });

        return areas;
    }

    protected maxDepth: number;
    protected previousId: string | null = null;

    /**
     * Recursively sets processes for the given areaNode, by filtering processes based on roles and area.
     * It also adds nested placeholders up to the maximum depth of the tree, excluding 'placeholder' nodes.
     * @param areaNode The area node to set processes for.
     * @param currentDepth The current depth in the tree, starting from 1.
     * @returns The modified areaNode with processes and nested placeholders.
     */
    protected setProcessesForArea(areaNode: ExtendedArea, currentDepth: number = 1): ExtendedArea {
        if (!areaNode) {
            throw new Error('areaNode is null or undefined');
        }

        // Helper function to calculate maximum depth of the areaNode tree, excluding 'placeholder' nodes
        const getMaxDepth = (node: ExtendedArea): number => {
            if (!node) {
                return 0;
            }

            // Skip nodes with id 'placeholder'
            if (node.id === 'placeholder') {
                return 0;
            }

            if (!node.children || node.children.length === 0) {
                return 1;
            }

            // Recursively calculate depth, excluding 'placeholder' nodes in children
            return 1 + Math.max(...node.children.map(child => getMaxDepth(child)));
        };

        // Calculate the maximum depth only once at the start, excluding 'placeholder' nodes
        if (this.maxDepth === undefined && areaNode.id) {
            this.maxDepth = getMaxDepth(areaNode);
        }

        // Recursive function to create nested placeholders up to the target depth
        const createNestedPlaceholders = (
            currentDepth: number,
            targetDepth: number,
            processes: any[]
        ): ExtendedArea => {
            if (currentDepth >= targetDepth) {
                // Base case: at max depth, create a placeholder node with the processes
                return {
                    id: 'placeholder',
                    type: 'area',
                    processes: processes,
                };
            } else {
                // Recursive case: create a placeholder node containing another placeholder node
                return {
                    id: 'placeholder',
                    type: 'area',
                    children: [createNestedPlaceholders(currentDepth + 1, targetDepth, processes)],
                };
            }
        };

        // Filter the processes based on roles and area
        const filteredProcesses = Object.values(
            this._processService.state
                .filter(process => {
                    if (!process) return false;

                    const isActive = ['A', 'ACTIVE'].includes(process.status?.toUpperCase() || '');

                    return process.type === 'process' && process.area === areaNode.id && isActive;
                })
                .reduce(
                    (acc, process) => {
                        // Keep only the latest version for each wbs
                        if (!acc[process.wbs] || +acc[process.wbs].version < +process.version) {
                            acc[process.wbs] = process;
                        }
                        return acc;
                    },
                    {} as Record<string, (typeof this._processService.state)[number]>
                )
        ).sort((a, b) => a.internalId.localeCompare(b.internalId));

        // If there are filtered processes and areaNode has children
        if (
            filteredProcesses.length &&
            areaNode.children &&
            !areaNode.children.some(item => item.id === 'placeholder')
        ) {
            // Add nested placeholders up to the max depth, starting from the current depth
            const nestedPlaceholders = createNestedPlaceholders(
                currentDepth,
                this.maxDepth,
                filteredProcesses
            );
            areaNode.children = [nestedPlaceholders, ...areaNode.children];
        } else if (
            filteredProcesses.length &&
            (!areaNode.children || !areaNode.children.some(item => item.id === 'placeholder'))
        ) {
            // If there are no children, directly assign processes to the areaNode
            areaNode.processes = filteredProcesses;
        }

        // Recursively apply the process to each child if children exist
        if (areaNode.children) {
            areaNode.children = areaNode.children.map(child =>
                this.setProcessesForArea(child, currentDepth + 1)
            );
        }
        return areaNode;
    }

    /**
     * Initializes the component by setting the appropriate business process service
     * based on the `bpServiceType`. It also configures the test view state in the
     * process tree service depending on the type of process.
     */
    ngOnInit(): void {
        this._processTreeService.setIsTestView(false);
        if (this.bpServiceType === 'task') {
            this.bpService = this.inject.get(TaskProcessService);
        } else if (this.bpServiceType === 'view') {
            this.bpService = this.inject.get(ViewService);
            if (this.type === TypeOfProcess.Scenario) {
                this._processTreeService.setIsTestView(true);
            }
        } else if (this.bpServiceType === 'bpReport') {
            this.bpService = this.inject.get(BpReportService);
        } else if (this.bpServiceType === 'verify') {
            this.bpService = this.inject.get(VerifyService);
        } else {
            this.bpService = this.inject.get(BusinessProcessTaskService);
        }
    }

    ngOnDestroy(): void {
        this.languageChangerService.unregister(this.componentName);
    }

    /**
     * Subscribes to route query parameter map changes. When a change is detected, it waits for 100 ms and then calls
     * handleTreeNodeSelection with the new parameters. This is necessary because the tree component (and thus the
     * handleTreeNodeSelection method) is not yet fully initialized when the parameter map changes.
     */
    protected subscribeToParamChange(): void {
        this.route.queryParamMap
            .pipe(
                takeUntilDestroyed(this._destroyRef),
                map((params: ParamMap) => {
                    setTimeout(() => {
                        this.handleTreeNodeSelection(params);
                    }, 100);
                    return params;
                })
            )
            .subscribe();
    }

    treeNodeSelectionChanged(node: ProcessFlatTreeNode) {
        setTimeout(() => {
            this._processService.selectedFlatTreeNode.set(node);
        });
    }

    /**
     * Check if the user can change the tree node selection or not.
     *
     * This method checks if the user has unsaved changes and if the user can change
     * the tree node selection. If the user has unsaved changes, it will ask the user
     * if they want to continue without saving. If the user chooses to continue without
     * saving, the method will return true. If the user chooses to save, the method
     * will return false.
     *
     * @param node The tree node that the user wants to select.
     * @returns An observable that emits a boolean value. If the value is true, the user
     * can change the tree node selection. If the value is false, the user cannot
     * change the tree node selection.
     */
    canChangeTreeNodeSelection(node: ProcessFlatTreeNode): Observable<boolean> | boolean {
        let canChange = new Subject<boolean>();

        const finalUrl = this._processMenuService.buildNodeRelativeLink(node);

        // set id of the selected node in the url
        this.orgNav.navigateByUrl(finalUrl);

        let continueWithoutSaving = this._saveButtonService.continueWithoutSaving
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: value => {
                    canChange.next(value);
                    continueWithoutSaving.unsubscribe();
                },
            });

        return canChange.asObservable();
    }

    /**
     * Handles the selection of a tree node based on query parameters.
     *
     * This function takes query parameters, retrieves the current process state,
     * and selects the appropriate business process or scenario based on the
     * provided 'bpId' or 'tscId'. If a new business process ID is detected, it
     * updates the `previousBpId` and selects the business process. If a scenario
     * ID is provided, it selects the corresponding scenario.
     *
     * @param params - The parameter map containing query parameters that include
     *                 'bpId' for business process ID and 'tscId' for test scenario ID.
     */
    async handleTreeNodeSelection(params: ParamMap) {
        const processState = this._processService._state();
        const bpId = this.route.snapshot.params['id'];
        const tscId = params.get('tscId');

        if (bpId && this.previousBpId !== bpId) {
            this.selectBusinessProcess(bpId, processState);
        } else if (tscId) {
            this.selectScenario(tscId, processState);
        }
    }

    /**
     * Selects a business process by its ID and updates the selected process.
     *
     * The function first locates the process with the specified ID from the process state.
     * If found, it sets the process as the selected process.
     * If the process service is not currently validating and the type is Task,
     * it fetches the business process details by its ID.
     *
     * @param id - The ID of the business process to select.
     * @param processState - The current state of processes to search for the associated process.
     */
    protected selectBusinessProcess(id: string, processState: Process[]) {
        if (processState.length === 0) {
            return;
        }
        const process = processState.find(el => el.id === id);
        if (!process) {
            this.alertService.errorAlert('errors', 'processNotFound', 'notFound');
            return;
        }

        this._processService.setSelectedProcess(process);

        this.previousBpId = id;

        if (this.type === TypeOfProcess.Task && !this._processService.isValidationOngoing) {
            // this.bpService.getAndSetBusinessProcessById(id);
            this.orgNav.navigate([...this.baseUrl, id]);
        }
    }

    /**
     * Selects a scenario by its ID and updates the selected process.
     *
     * The function first locates the scenario with the specified ID from the test scenarios.
     * If found, it retrieves the associated process using the scenario's business process ID.
     * Once the process is located, it sets the process as the selected process.
     * If the process service is not currently validating, it fetches the business process
     * details by its ID. Additionally, it sets the selected scenario with a delay to avoid
     * overwriting issues.
     *
     * @param id - The ID of the scenario to select.
     * @param processState - The current state of processes to search for the associated process.
     */
    protected selectScenario(id: string, processState: Process[]) {
        const scenario = this._testService.testScenarios.find(el => el.id === id);
        if (!scenario) return;

        const processItem = processState.find(el => el.id === scenario.bp_id);
        if (!processItem) return;

        this._processService.setSelectedProcess(processItem);

        if (!this._processService.isValidationOngoing) {
            this.bpService.getAndSetBusinessProcessById(processItem.id);
        }

        // Optional: configurable delay instead of hardcoded timeout
        this.delayedSetSelectedScenario(scenario, 50); // 50 ms delay, configurable as needed
    }

    /**
     * Sets the selected scenario on the TestScenariosService after a delay,
     * which is necessary to avoid overwriting the newly selected scenario with the previously
     * selected one, which is still present in the url.
     *
     * @param scenario - The scenario to set as selected.
     * @param delay - The time in milliseconds to wait before setting the scenario as selected.
     */
    protected delayedSetSelectedScenario(scenario: TestScenario, delay: number) {
        setTimeout(() => {
            this._testScenarioService.selectedScenario = scenario;
        }, delay);
    }

    /**
     * Reorders the wbs property of the given ProcessTreeNode and its children if the list was dropped.
     *
     * @param $event - The ProcessTreeNode list that was dropped.
     */
    dropListDrop($event: any) {
        $event = this._processTreeService.reorderWbs($event, 0);
    }

    /**
     * Handles a menu item click event by delegating the event to the ProcessMenuService.
     *
     * @param $event - The context menu item click data containing the node and menu item information.
     */
    menuItemClicked($event: ContextMenuItemClickData<ProcessFlatTreeNode>) {
        this._processMenuService.menuItemClicked($event);
    }

    async corruptTreeDataFixed(processes: Array<Process>): Promise<void> {
        await Promise.all(
            processes.map(async process => {
                const res = await this._processService.updateBpl(process).toPromise();
            })
        );

        this._processService.initializeData();
    }
}
